import React, { useState, useEffect } from "react";
import { DataTable, Badge, Button, Pagination } from "../../../components/ui";
import { fmt } from "../../../lib/formatters";
import { 
  Eye, Edit, RefreshCcw, RotateCcw, 
  Check, DollarSign, Plus, FileText 
} from "lucide-react";

/**
 * UnifiedReturnsTable component
 * Refactored to use config-driven DataTable and reusable UI components.
 */
export const UnifiedReturnsTable = ({ 
  returns, 
  subTab, 
  onView, 
  onEdit, 
  onViewPayment, 
  onConfirm,
  onRestock
}) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [returns]);

  const rows = returns || [];
  const isMaster = subTab === "master" || subTab === "MASTER";
  const columns = [
    {
      header: isMaster ? "Master Returns" : "Invoice",
      render: (r) => (
        <span className="text-[11px] font-bold text-slate-700 tracking-wider uppercase">
          {r.rawType === 'sale' ? r.invoiceNo : r.returnId}
        </span>
      )
    },
    {
      header: isMaster ? "Return Type" : "Status / Reason",
      render: (r) => isMaster ? (
        <Badge status={r.rawType === 'purchase' ? 'APPROVED' : 'PENDING'}>
          {r.returnType === 'Purchase' || r.returnType === 'Purchase Return' ? 'Purchase Return' : 'Site Return'}
        </Badge>
      ) : (
        <span className="text-[12px] font-medium text-slate-600 italic">{r.reason}</span>
      )
    },
    {
      header: isMaster ? "Reason" : "Date",
      render: (r) => isMaster ? (
        <span className="text-[12px] font-medium text-slate-600">{r.reason || r.remarks || r.description || "General Return"}</span>
      ) : (
        <span className="text-[12px] text-slate-500 font-medium">{r.date ? new Date(r.date).toLocaleDateString() : "—"}</span>
      )
    },
    {
      header: isMaster ? "Party Details" : "Items",
      render: (r) => isMaster ? (
        <div className="flex flex-col">
          <span className="font-semibold text-slate-800 text-[12px] truncate block max-w-[150px]">{r.party || "—"}</span>
          <span className="text-[10px] text-slate-400 font-normal uppercase tracking-tight">{r.rawType === 'purchase' ? 'Vendor' : 'Project Site'}</span>
        </div>
      ) : (
        <div className="flex flex-col">
          <span className="font-semibold text-slate-800 text-[12px] truncate block max-w-[180px]">{r.itemSummary || "No Items"}</span>
          <span className="text-[10px] text-slate-400 font-normal uppercase tracking-tight">
            {r.rawType === 'purchase' ? `Vendor: ${r.party}` : `Site: ${r.party}`}
          </span>
        </div>
      )
    },
    {
      header: "Return Amount",
      render: (r) => <span className="text-[12px] font-medium text-[#f97316]">{fmt(r.amount)}</span>
    },
    {
      header: "Received Amount",
      render: (r) => <span className="text-[12px] font-medium text-emerald-600">{fmt(r.receivedAmount || 0)}</span>
    }
  ];

  if (!isMaster) {
    if (subTab === "site") {
      columns.push({
        header: "Project ID",
        render: (r) => (
          <div className="flex items-center gap-1.5 text-[10px] font-normal uppercase tracking-tight text-[#f97316] bg-orange-50 w-fit px-2 py-0.5 rounded border border-orange-100">
            {r.projectId || r.invoiceNo || "N/A"}
          </div>
        )
      });
    }
  }

  columns.push({
    header: "Action",
    render: (r) => (
      <div className="flex gap-1.5 items-center">
        <Button variant="ghost" size="icon-xs" onClick={() => onView(r)} title="View Details">
          <Eye size={14} />
        </Button>

        {/* Dollar icon shown for all non-master tables */}
        {!isMaster && r.refundStatus !== 'received' && (
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
            onClick={() => onViewPayment(r)}
            title="Record Refund Payout"
          >
            <DollarSign size={14} />
          </Button>
        )}

        {/* Plus icon to confirm stock receipt */}
        {(!r.status || ( (r.status.toUpperCase() !== 'RECEIVED' || r.rawType === 'purchase') && r.status.toUpperCase() !== 'RESTOCKED' && r.status.toUpperCase() !== 'COMPLETED')) && onRestock && (
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
            onClick={() => onRestock(r)}
            title="Confirm Stock Receipt"
          >
            <Plus size={16} strokeWidth={2.5} />
          </Button>
        )}
      </div>
    )
  });

  const currentData = rows.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil(rows.length / itemsPerPage), 1);

  return (
    <div className="card rounded-none overflow-hidden border-slate-100 shadow-sm">
      <DataTable columns={columns} data={currentData} />
      
      {rows.length > itemsPerPage && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* {rows.length === 0 && (
        <div className="p-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
          <p className="text-slate-400 font-medium">No return records found</p>
        </div>
      )} */}
    </div>
  );
};

